"use client";

import { Tabs } from "@/components/ui/tabs";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { VocabularyCategorySection } from "@/components/settings/VocabularyCategorySection";
import { VocabularyPreviewDrawer } from "@/components/ui/vocabulary-preview-drawer";
import { LanguageSelector } from "@/components/settings/LanguageSelector";
import { CefrLevelSelector } from "@/components/settings/CefrLevelSelector";
import { Toast } from "@/components/ui/toast";
import { useVocabularySettings } from "@/hooks/useVocabularySettings";
import { useVocabularyPreview } from "@/hooks/useVocabularyPreview";
import { useLanguageSettings } from "@/hooks/useLanguageSettings";
import { getCategoriesByTab } from "@/lib/constants/vocabulary-categories";
import React from "react";
import { useI18n } from "@/lib/i18n";
import { ShadowingGoalSection } from "@/components/settings/ShadowingGoalSection";
import { ScrollablePills } from "@/components/ui/scrollable-pills";
// import { MobileMenuButton } from "@/components/ui/mobile-menu-button";
import { VideoTitlePreference } from "@/components/settings/VideoTitlePreference";
import { ThemeSelector } from "@/components/settings/ThemeSelector";
import { UserProfileSection } from "@/components/settings/UserProfileSection";
import { useIsMobile } from "@/hooks/use-mobile";
import { LanguageToggle } from "@/components/language-toggle";


export default function Page() {
  const { t, lang } = useI18n() as any;
  const isMobile = useIsMobile();
  // Helper to check if current language is Chinese (supports both old and new format)
  const isChinese = lang === 'chinese' || lang === 'zh';
  const {
    // State
    selectedTab,
    setSelectedTab,
    selectedExamTab,
    setSelectedExamTab,
    settingsLoaded,
    selectedDictionary,
    isLoading,
    error,
    success,
    
    // Functions
    isCategorySelected,
    handleVocabularyCardClick,
    handleExamCardClick,
    handleRemoveSelection,
    clearMessages,
    
    // Constants from hook
    examTabs,
    examCardsData,
  } = useVocabularySettings();


  const {
    isPreviewOpen,
    previewData,
    openPreview,
    closePreview,
  } = useVocabularyPreview();

  const {
    currentLanguage,
    isLoading: isLanguageLoading,
    updateLanguage,
    languageOptions,
  } = useLanguageSettings();

  const isNativeChinese = currentLanguage === 'zh';

  // const mobileMenuButton = (
  //   <MobileMenuButton ariaLabel={lang === 'chinese' ? '打开菜单' : 'Open menu'} />
  // );

  return (
    <AuthGuard>
      {/* {mobileMenuButton} */}
      {/* Toast notifications - outside main container for proper z-index */}
      {error && (
        <Toast
          message={error}
          type="error"
          onClose={clearMessages}
        />
      )}
      {success && (
        <Toast
          message={success}
          type="success"
          onClose={clearMessages}
        />
      )}

      
      <div className="relative py-4 sm:py-6 lg:py-12 px-3 sm:px-4 lg:px-6 mx-auto">
        {/* Add CSS animation styles */}
        <style jsx global>{`
          @keyframes shimmer {
            100% {
              transform: translateX(100%);
            }
          }
          
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          
          /* tabs 容器水平滚动 */
          .tabs-scroll-container {
            overflow-x: scroll !important;
            overflow-y: hidden !important;
            -webkit-overflow-scrolling: touch !important;
            touch-action: pan-x !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            box-sizing: border-box !important;
            position: relative !important;
          }
          
          .tabs-scroll-container::-webkit-scrollbar {
            display: none !important;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .animate-pulse,
            .animate-in,
            .transition-all {
              animation: none !important;
              transition: none !important;
            }
          }
        `}</style>
      <section>
        <header className="pb-4 sm:pb-4 space-y-3 sm:space-y-4 mb-3">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground">{t('settings.title')}</h1>
          {/* <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Customize your learning experience with language preferences and vocabulary selections.
          </p> */}
        </header>
      </section>

      {/* User Profile Section - Only on Mobile */}
      {isMobile && (
        <section className="mb-6">
          <UserProfileSection />
        </section>
      )}

      {/* Native Language Setting */}
      <section className="mb-6">
        <div className="mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{t('settings.nativeLanguage')}</h2>
          <p className={`${isChinese ? 'text-xs sm:text-sm font-normal' : 'text-sm sm:text-base'} text-muted-foreground mb-4`}>{t('settings.nativeLanguageHint')}</p>
          <div className="space-y-4">
            <LanguageSelector 
              currentLanguage={currentLanguage}
              isLoading={isLanguageLoading}
              languageOptions={languageOptions}
              onSelect={updateLanguage}
            />
            <CefrLevelSelector />
          </div>
        </div>
      </section>

      {/* Video title preference */}
      {/* <section className="mb-6">
        <VideoTitlePreference />
      </section> */}

      {/* Theme Setting */}
      <section className="mb-6">
        <div className="mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{t('settings.theme.title', 'Appearance')}</h2>
          <p className={`${isChinese ? 'text-xs sm:text-sm font-normal' : 'text-sm sm:text-base'} text-muted-foreground mb-4`}>
            {t('settings.theme.hint', 'Choose your preferred color theme')}
          </p>
        </div>
        <ThemeSelector />
      </section>

      {/* Interface Language Setting */}
      <section className="mb-6">
        <div className="mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{t('settings.interfaceLanguage', 'Interface Language')}</h2>
          <p className={`${isChinese ? 'text-xs sm:text-sm font-normal' : 'text-sm sm:text-base'} text-muted-foreground mb-4`}>
            {t('settings.interfaceLanguageHint', 'Choose the language for the app interface. This is independent from your native language setting.')}
          </p>
        </div>
        <div className="flex items-center">
          <LanguageToggle collapsed={false} />
        </div>
      </section>

      {/* Shadowing Goal Setting */}
      <ShadowingGoalSection />

      {/* Vocabulary Dictionary Setting */}
      {isNativeChinese ? (
        <>
          <section className="mb-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground">{t('settings.vocabularyDict')}</h2>
                {selectedDictionary && (
                  <button
                    onClick={handleRemoveSelection}
                    disabled={isLoading}
                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-red-200/60 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    <svg className="w-3.5 h-3.5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {isLoading ? t('settings.removing') : t('settings.remove')}
                  </button>
                )}
              </div>
              <p className={`${isChinese ? 'text-xs sm:text-sm font-normal' : 'text-sm sm:text-base'} text-muted-foreground`}>{t('settings.selectVocabularyHint')}</p>
            </div>
          </section>

          <div>
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <div className="relative mb-4 sm:mb-6">
                <ScrollablePills
                  value={selectedTab}
                  onChange={setSelectedTab}
                  items={[
                    { value: "college", label: "大学英语" },
                    { value: "graduate", label: "考研" },
                    { value: "professional", label: "专业英语" },
                    { value: "pet", label: "PET" },
                    { value: "self", label: "自考英语二" },
                    { value: "other", label: "其他" },
                  ]}
                />
              </div>
            </Tabs>
          </div>

          {/* Vocabulary categories section */}
          <VocabularyCategorySection
            categories={getCategoriesByTab(selectedTab)}
            isLoading={!settingsLoaded}
            isSelected={isCategorySelected}
            onCardClick={handleVocabularyCardClick}
            onPreview={openPreview}
            skeletonCount={8}
            error={error}
            isCardLoading={isLoading}
          />
          
          {/* Exam categories section */}
          <div className="mt-8 sm:mt-10">
            <div className="mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{t('settings.examVocab.title', 'Exam Vocabulary')}</h2>
              <p className="text-sm sm:text-base text-muted-foreground">{t('settings.examVocab.subtitle', 'Professional and standardized exam vocabulary')}</p>
            </div>
            <Tabs value={selectedExamTab} onValueChange={setSelectedExamTab}>
              <div className="relative mb-4 sm:mb-6">
                <ScrollablePills
                  value={selectedExamTab}
                  onChange={setSelectedExamTab}
                  items={examTabs.map((tab: any) => ({ value: tab.value, label: tab.label }))}
                />
              </div>
            </Tabs>
            
            <VocabularyCategorySection
              categories={examCardsData[selectedExamTab] || []}
              isLoading={!settingsLoaded}
              isSelected={isCategorySelected}
              onCardClick={handleExamCardClick}
              onPreview={openPreview}
              skeletonCount={4}
              error={error}
              isCardLoading={isLoading}
            />
          </div>

          {/* Vocabulary Preview Drawer */}
          {previewData && (
            <VocabularyPreviewDrawer
              isOpen={isPreviewOpen}
              onClose={closePreview}
              dictionaryFile={previewData.dictionaryFile}
              categoryTitle={previewData.categoryTitle}
            />
          )}
        </>
      ) : (
        <section className="mb-6">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">{t('settings.vocabularyDict')}</h2>
            <p className={`${isChinese ? 'text-xs sm:text-sm font-normal' : 'text-sm sm:text-base'} text-muted-foreground`}>
              {isChinese 
                ? '目前词书仅支持中文母语，请将母语设置为中文以解锁词书。' 
                : 'Vocabulary dictionaries are available when your native language is set to Chinese.'}
            </p>
          </div>
        </section>
      )}
    </div>
    </AuthGuard>
  );
}
